// İSG uzmanı portalı — Güvenlik / Saha Geçişi (Security) erişim sondası (route guard UX).
// `visitsAccess.ts`'in birebir deseni (S32 tasarım spec'i §9).
//
// Asıl yetki backend'dedir: her Güvenlik ucu `[ModuleAction("security", …)]` taşır; grant'ı
// olmayan principal → 403 (api-contract §29). Bu sonda yalnız UX görünürlüğüdür.
//
// Sonda ucu `GET /security/sites` (`security:get`) — PII taşımaz, aynı zamanda FE'nin saha
// seçicisidir (tek istek, iki iş):
//   - 2xx  → erişim var (granted) — saha listesi de döner (sayfa yeniden kullanır)
//   - 403  → yetki yok (forbidden) → gizli menü + 403 sayfası
//   - diğer (404/503/ağ) → servis/veri kullanılamıyor (unavailable) → yeniden dene
//
// **ÖNEMLİ — bu sonda yalnız `security:get`i kapsar.** `gate-result` (kapı kodu yoklaması +
// gün listesi) YENİ bir aksiyondur ve seed'de grant'sız gelir (K4, fail-closed) — bu sondanın
// `granted` dönmesi `gate-result`'ın da açık olduğu anlamına GELMEZ. O durum ayrıca, ilgili
// sekmelerin kendi 403 işleyişiyle ele alınır (bkz. `GateCodeTab.tsx`/`DayListTab.tsx`, tasarım
// spec'i §8.4).

import { ApiError, type SecuritySiteDto, securityApi } from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { securityKeys } from './securityKeys';

export type SecurityAccessStatus = 'loading' | 'granted' | 'forbidden' | 'unavailable';

export interface SecurityAccess {
  status: SecurityAccessStatus;
  /** Firmanın sahaları (yalnız `granted` iken dolu; saha seçici bunu yeniden kullanır). */
  sites: SecuritySiteDto[];
  /** `unavailable` durumunda yeniden denemek için. */
  refetch: () => void;
}

export function useSecurityAccess(): SecurityAccess {
  const query = useQuery({
    queryKey: securityKeys.sites(),
    queryFn: () => securityApi.listSites(),
    retry: false,
    staleTime: 5 * 60_000,
  });

  let status: SecurityAccessStatus;
  if (query.isPending) {
    status = 'loading';
  } else if (query.isSuccess) {
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 403) {
    status = 'forbidden';
  } else {
    status = 'unavailable';
  }

  return { status, sites: query.data ?? [], refetch: () => void query.refetch() };
}
